import { PERMISSION_NAME } from 'utils/constants';

export const transformInviteUserFormData = (formData) => {
    if (!formData) return null;
    const { email, username, active = false, orgStates = {} } = formData;

    // Transform user data
    const user = {
        username: username?.trim(),
        email: email?.trim(),
        active: active ?? false,
    };

    // Transform location feature permissions
    const locationFeaturePermissions = [];

    // Iterate through each organization's locations
    Object.values(orgStates).forEach((org) => {
        if (!org.enabled) return;

        Object.entries(org.locations).forEach(([locationId, location]) => {
            if (!location.enabled) return;

            const features = [];
            const listOrg = location?.accessibleOrgIds || [];
            const authorizedOrgs = listOrg.map((org) => org.value ?? org);

            // Transform each feature's permissions
            Object.entries(location.permissions).forEach(([featureId, featurePermissions]) => {
                // Only add features that have permissions

                if (featurePermissions.permissions?.length > 0) {
                    features.push({
                        id: parseInt(featureId),
                        permissions: featurePermissions.permissions,
                    });
                }
            });
            locationFeaturePermissions.push({
                location: {
                    id: parseInt(locationId),
                },
                features,
                accessibleOrgIds: authorizedOrgs,
            });
        });
    });

    return {
        user,
        locationFeaturePermissions,
    };
};

export const formatUserPermissionDetail = ({ userPermissionDetail, featuresAndOrgs }) => {
    if (!userPermissionDetail || !featuresAndOrgs) return null;
    const { organizations = [], features: allFeatures = [] } = featuresAndOrgs;

    const { user = {}, locationFeaturePermissions = [] } = userPermissionDetail;
    const userFeaturesObj = {};
    const organizationsObj = {};

    const orgStates = locationFeaturePermissions.reduce((acc, { location, features: locationFeatures, accessibleOrgIds }) => {
        if (!location) return acc;

        const { id: locationId, yardName, organization } = location;
        const { id: orgId, name: orgName } = organization || {};

        if (!orgId) return acc;
        if (!organizationsObj[orgId]) {
            organizationsObj[orgId] = { id: orgId, name: orgName };
        }

        if (!acc[orgId]) {
            acc[orgId] = { orgId, orgName, enabled: true, locations: {} };
        }

        if (!acc[orgId].locations[locationId]) {
            acc[orgId].locations[locationId] = { id: locationId, enabled: true, yardName, permissions: {}, accessibleOrgIds };
        }

        locationFeatures.forEach((feature) => {
            const { id: featureId, name, displayName, permissions } = feature;
            // Collect userFeature info for further display
            userFeaturesObj[featureId] = {
                id: featureId,
                name,
                displayName,
            };
            // Set form state for this feature
            acc[orgId].locations[locationId].permissions[featureId] = {
                viewOnly: permissions.some((p) => p.name === PERMISSION_NAME.VIEW),
                fullAccess: permissions.some((p) => p.name === PERMISSION_NAME.FULL_ACCESS),
                name,
                permissions,
            };
        });

        // search for features that the user have no saved data
        //  then add it to the form state with default checked values
        allFeatures.forEach((feature) => {
            const { id: featureId, name, displayName } = feature;
            if (acc[orgId].locations[locationId].permissions[featureId]) {
                // already have saved data => skip
                return;
            }
            // Collect userFeature info for further display
            userFeaturesObj[featureId] = {
                id: featureId,
                name,
                displayName,
            };
            // Set form state for this feature
            acc[orgId].locations[locationId].permissions[featureId] = {
                viewOnly: false, // unchecked by default
                fullAccess: false, // unchecked by default
                name: feature.name,
                permissions: [], // empty by default
            };
        });

        return acc;
    }, {});

    // Add missing locations not returned from API
    organizations.forEach(({ id: orgId, locations }) => {
        if (!orgStates[orgId]) return;

        locations.forEach(({ id: locationId, yardName }) => {
            if (!orgStates[orgId].locations[locationId]) {
                orgStates[orgId].locations[locationId] = {
                    id: locationId,
                    enabled: false,
                    yardName,
                    permissions: {},
                    accessibleOrgIds: [],
                };
            }
        });
    });

    const userFeaturesList = Object.values(userFeaturesObj);
    const organizationsList = Object.values(organizationsObj);
    return {
        email: user.email,
        username: user.username,
        active: user.active,
        orgStates,
        features: userFeaturesList,
        organizations: organizationsList,
    };
};
